import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  registerFormSchema,
  RegisterFormData,
} from "../schemas/registerFormSchema";
import { sendWelcomeEmail } from "@/services/notification/notificationService";
import { analyticsEvents } from "@/services/analytics";

// Helper function to clean up auth state
const cleanupAuthState = () => {
  // Remove any lingering auth tokens before registration
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("supabase.auth.") || key.includes("sb-")) {
      localStorage.removeItem(key);
    }
  });
  // Also check sessionStorage if in use
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith("supabase.auth.") || key.includes("sb-")) {
      sessionStorage.removeItem(key);
    }
  });
};

export function useRegisterForm(
  isStaffInvite: boolean = false,
  onAuthComplete?: () => void,
  defaultEmail: string | null = null
) {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: defaultEmail || "",
      password: "",
      confirmPassword: "",
      terms: false,
    },
    mode: "onChange",
  });

  // Update email field if defaultEmail changes
  useEffect(() => {
    if (defaultEmail) {
      form.setValue("email", defaultEmail);
    }
  }, [defaultEmail, form]);

  // Access formState directly to determine validity
  const isValid = form.formState.isValid;

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);

      // Clean up any existing auth state to avoid conflicts
      // cleanupAuthState();

      // Set the appropriate role based on whether this is a staff invitation or a regular registration
      const userRole = isStaffInvite ? "staff" : "manager";

      // Sign up with Supabase Auth with role assignment
      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            role: userRole, // Set role based on registration type
          },
        },
      });

      if (error) {
        console.error("Registration error details:", error);
        let errorMessage = error.message;

        // Track failed registration
        analyticsEvents.auth.register("failed");

        // Provide more user-friendly error messages for common errors
        if (
          error.message.includes("already registered") ||
          error.message.includes("Database error saving")
        ) {
          // Try signing in instead if the user already exists
          if (isStaffInvite) {
            const { data: signInData, error: signInError } =
              await supabase.auth.signInWithPassword({
                email: data.email,
                password: data.password,
              });

            if (!signInError && signInData.user) {
              if (onAuthComplete) {
                onAuthComplete();
              }

              setIsLoading(false);
              return;
            }

            errorMessage =
              "This email is already registered. Please sign in with your existing password instead.";
          } else {
            errorMessage =
              "This email is already registered. Please try signing in instead.";
          }
        }

        toast({
          variant: "destructive",
          title: "Registration failed",
          description: errorMessage,
        });
        setIsLoading(false);
        return;
      }

      if (!authData.user) {
        console.error("No user returned from signUp");

        // Track failed registration - no user
        analyticsEvents.auth.register("failed_no_user");

        toast({
          variant: "destructive",
          title: "Registration failed",
          description: "No user data was returned. Please try again.",
        });
        setIsLoading(false);
        return;
      }
      // Track successful registration
      analyticsEvents.auth.register(
        isStaffInvite ? "staff_invite" : "standard"
      );

      // Send welcome email
      try {
        await sendWelcomeEmail(data.email, data.firstName, data.lastName);
        toast({
          title: "Welcome email sent",
          description: "Check your inbox for a welcome message.",
        });
      } catch (emailError) {
        console.error("Failed to send welcome email:", emailError);
        // We don't want to block the sign-up process if the email fails to send
      }

      // Different flow based on registration type
      if (isStaffInvite) {
        // Staff invitation flow - call the callback which will handle the invitation acceptance
        if (onAuthComplete) {
          // Delay to ensure auth state is updated properly
          setTimeout(() => {
            onAuthComplete();
          }, 1000);
        } else {
          // Fallback navigation if no callback provided
          navigate("/dashboard/staff");
        }
      } else {
        // For regular manager registration, trigger the store creation wizard via callback
        if (onAuthComplete) {
          onAuthComplete();
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Unhandled registration error:", error);

      // Track unhandled registration error
      analyticsEvents.auth.register("error");

      toast({
        variant: "destructive",
        title: "Registration failed",
        description: error.message || "Something went wrong. Please try again.",
      });
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading,
    isValid,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
